import {useEffect} from 'react';
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {tagsAPI, HTTPS_DEPENDENT_CHECK_KEYS} from 'entities/monitor';
import {httpMonitorAPI} from 'features/http/api/httpMonitorAPI';
import {buildHttpMonitorPayload, getHttpMonitorFormDefaults} from 'features/http/lib/httpMonitorFormHelper';
import CreateHttpMonitorType from 'features/http/model/HttpMonitor/CreateHttpMonitorType';
import IHttpMonitorOutput from 'features/http/model/HttpMonitor/IHttpMonitor';
import {useIsFreeTariff, userSelector} from 'entities/user';
import {reachGoal, YM_GOALS} from 'shared/lib/analytics';
import {isTranslate} from 'shared/lib/lang';

import {useHeadersServerError} from './useHeadersServerError';

interface IUseHttpMonitorFormParams {
    item?: IHttpMonitorOutput,
    onSuccess: () => void,
}

export interface IUseHttpMonitorForm {
    form: UseFormReturn<CreateHttpMonitorType>,
    isNew: boolean,
    isSubmitting: boolean,
    freeTariff: boolean,
    isHttpUrl: boolean,
    submit: ReturnType<UseFormReturn<CreateHttpMonitorType>['handleSubmit']>,
}

export const useHttpMonitorForm = ({item, onSuccess}: IUseHttpMonitorFormParams): IUseHttpMonitorForm => {
    const user = useSelector(userSelector);
    const [addHttpMonitor, {isLoading: isLoadingCreate}] = httpMonitorAPI.useAddHttpMonitorMutation();
    const [updateHttpMonitor, {isLoading: isLoadingUpdate}] = httpMonitorAPI.useUpdateHttpMonitorMutation();
    const {refetch: refetchTags} = tagsAPI.useGetTagsQuery();
    const freeTariff = useIsFreeTariff();
    const isNew = !item;

    const form = useForm<CreateHttpMonitorType>({
        mode: 'onChange',
        defaultValues: getHttpMonitorFormDefaults(item, user, freeTariff, isTranslate()),
    });
    const {setValue, watch} = form;
    const handleSubmitError = useHeadersServerError(form);

    const url = watch('url');
    const isHttpUrl = /^http:\/\//i.test((url ?? '').trim());

    useEffect(() => {
        if (!isNew || !isHttpUrl) {
            return;
        }
        HTTPS_DEPENDENT_CHECK_KEYS.forEach(key => setValue(key, false));
    }, [isNew, isHttpUrl, setValue]);

    const onSubmit: SubmitHandler<CreateHttpMonitorType> = async(values) => {
        const data = buildHttpMonitorPayload(values);

        try {
            if (isNew) {
                await addHttpMonitor(data).unwrap();
                reachGoal(YM_GOALS.SUBMIT_FORM_ADD_MONITOR);
                toast(t`Монитор добавлен`);
                await refetchTags();
                onSuccess();
            } else {
                await updateHttpMonitor({
                    uuid: item?.uuid,
                    ...data,
                }).unwrap();
                await refetchTags();
                toast(t`Монитор изменен`);
                onSuccess();
            }
        } catch (error) {
            handleSubmitError(error);
        }
    };

    return {
        form,
        isNew,
        isSubmitting: isLoadingCreate || isLoadingUpdate,
        freeTariff,
        isHttpUrl,
        submit: form.handleSubmit(onSubmit),
    };
};

export default useHttpMonitorForm;
